import { ROOT_IMG_URL, FILE_ATTENTE_URL } from './GlobalValues';

/**
 * ══════════════════════════════════════════════════════════════════════════
 * CONTENU STATIQUE — décision 12 du plan de passation
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Chaque bloc éditable existe en deux versions : celle-ci, reprise mot pour mot
 * de la maquette, et un adaptateur richfield dans `src/richfields/adapters.js`.
 * Les vues rendent la version statique, la version richfield étant écrite juste
 * en dessous, en commentaire, prête à être décommentée bloc par bloc.
 *
 * Les composants de présentation (`src/ui/`) ignorent la provenance : ils
 * reçoivent les mêmes props dans les deux cas.
 *
 * Les questions restées ouvertes (§18 du plan) sont tranchées ici en statique,
 * comme demandé : « quand tu doutes on fait déjà le code statique ».
 */

const img = (name) => `${ROOT_IMG_URL}${name}`;
const collection = (name) => `${ROOT_IMG_URL}collection-2026/${name}`;

/* ─────────────────────────── Chrome ─────────────────────────── */

export const NAV_LINKS = [
  { label: 'Notre marque', href: '#' },
  { label: 'Nos services', href: '#' },
  { label: 'Nos produits', href: '#' },
  { label: 'Nos salons', href: '#' },
  { label: 'Collections', href: '#' },
  { label: "Astuces d'experts", href: '#' },
];

export const NAV_SECONDARY_LINKS = [
  { label: 'Tchip & moi', href: '#' },
  { label: 'Rejoignez-nous', href: '#' },
  { label: 'Ouvrez votre salon', href: '#' },
];

export const FOOTER_COLUMNS = [
  {
    title: 'Notre marque',
    links: [
      { label: 'Notre histoire', href: '#' },
      { label: 'Nos engagements', href: '#' },
      { label: 'Notre collection', href: '#' },
      { label: 'Rejoignez-nous', href: '#' },
    ],
  },
  {
    title: 'Nos services',
    links: [
      { label: 'Nos forfaits', href: '#' },
      { label: 'Nos couleurs', href: '#' },
      { label: 'Nos soins', href: '#' },
      { label: 'Nos produits', href: '#' },
    ],
  },
  {
    title: 'Nos salons',
    links: [
      { label: 'Trouver un salon', href: '/' },
      { label: "File d'attente", href: FILE_ATTENTE_URL },
      { label: 'Ouvrez votre salon', href: '#' },
      { label: 'tchip & moi', href: '#' },
    ],
  },
  {
    title: 'Légal',
    links: [
      { label: 'Mentions légales', href: '#' },
      { label: 'CGU', href: '#' },
      { label: 'Confidentialité', href: '#' },
      { label: 'Cookies', href: '#' },
    ],
  },
];

export const FOOTER_SOCIALS = [
  { key: 'facebook', label: 'Facebook', href: '#' },
  { key: 'instagram', label: 'Instagram', href: '#' },
  { key: 'youtube', label: 'YouTube', href: '#' },
  { key: 'tiktok', label: 'TikTok', href: '#' },
];

/**
 * §18 du plan : la maquette annonce « +250 salons » dans la modale, « +300 SALONS »
 * dans la réassurance et « 300 salons » dans le footer — trois valeurs en dur et
 * incohérentes entre elles. **Tranché par Romain (03/09) : dynamique sur
 * `brand.placeCount`**, arrondi à la dizaine inférieure la plus proche multiple
 * de 50 pour respecter l'esprit "+250/+300" de la maquette (un chiffre rond,
 * pas le total exact) tout en restant juste et cohérent aux trois emplacements.
 * `SALON_COUNT_FALLBACK` ne sert que si `brand.placeCount` est absent de l'état
 * (seed incomplet, mock) — ce module reste par ailleurs un filet de contenu
 * statique, mais ce chiffre-là n'a pas de raison de rester figé puisque la
 * donnée réelle est déjà disponible dans le state.
 */
export const SALON_COUNT_FALLBACK = 300;

export const roundedSalonCount = (placeCount) => Math.floor((placeCount || SALON_COUNT_FALLBACK) / 50) * 50;

export const BRAND_COPY = {
  searchModalSubtitle: (placeCount) => `+${roundedSalonCount(placeCount)} salons en France — aucun rendez-vous nécessaire`,
  footerDescription: (placeCount) =>
    `Des experts de la coiffure et produits professionnels au meilleur prix. ${roundedSalonCount(
      placeCount,
    )} salons partout en France. Accueil sans rendez-vous.`,
  copyright: '©2026 Tchip Coiffure • Tous droits réservés',
};

/* ─────────────────────── Blocs éditoriaux ─────────────────────── */

/** Bandeau « bons plans » — richfield PLAN_CANON sur la page détail (spec 3.d). */
export const PROMO_STRIP = {
  image: collection('23-05_TCHIP6533.jpg'),
  imageAlt: 'Offre balayage + soin du moment',
  eyebrow: 'Les bons plans canons',
  title: 'Balayage + soin signature à -20%',
  text: "Jusqu'au 30 juin, profitez de -20% sur le forfait balayage + soin L'Oréal Professionnel, dans la limite des places disponibles en file d'attente.",
};

/** Forfaits — richfield TARIFS sur la page détail (spec 3.e). */
export const PRICES = {
  eyebrow: 'Nos forfaits',
  title: { line1: 'Canon.', highlight: 'À prix canon.' },
  subtitle: 'Shampooing + coupe + brushing dans chaque forfait. Un prix, un résultat — sans surprise.',
  cta: { label: 'Détail des prestations', href: '#' },
  cards: [
    {
      image: img('stickers_prix/prix1.png'),
      title: 'Forfait Coupe',
      desc: 'Shampooing · Coupe · Brushing',
      badge: 'Courts 27 € · Longs 32 €',
    },
    {
      image: img('stickers_prix/prix2.png'),
      title: 'Forfait Couleur',
      desc: 'Shampooing · Coupe · Brushing · Couleur',
      badge: 'Courts 50 € · Longs 65 €',
      // Piège n°6 : la mise en avant et le ruban étaient positionnels et en dur
      // dans le CSS. Ce sont des données maintenant.
      isFeatured: true,
      ribbon: 'TOP DES VENTES',
    },
    {
      image: img('stickers_prix/prix3.png'),
      title: 'Forfait Balayage',
      desc: 'Shampooing · Coupe · Brushing · Balayage',
      badge: 'Courts 60 € · Longs 75 €',
    },
  ],
};

/** Expertises — richfield EXPERTISES sur la page détail (spec 3.g). */
export const EXPERTISES = {
  title: { line1: 'Ce que fait', highlight: 'ce salon' },
  subtitle:
    "Coupe, couleur, soin — nos coiffeurs maîtrisent toutes les techniques avec L'Oréal Professionnel et Kérastase.",
  link: { label: 'Toutes les prestations', href: '#' },
  cards: [
    {
      image: collection('23-05_TCHIP6198.jpg'),
      imageAlt: 'Coupe femme',
      title: 'Coupe & coiffage',
      text: "Des coupes adaptées à chaque morphologie, réalisées par des coiffeurs formés aux techniques L'Oréal Professionnel.",
      link: { label: 'Découvrir', href: '#' },
    },
    {
      image: collection('23-05_TCHIP6892.jpg'),
      imageAlt: 'Coloration et balayage',
      title: 'Coloration & balayage',
      text: 'Couleur pleine, balayage ou mèches : un résultat salon, prix canon, avec des produits professionnels.',
      link: { label: 'Découvrir', href: '#' },
    },
    {
      image: collection('23-05_TCHIP5901.jpg'),
      imageAlt: 'Soin cheveux',
      title: 'Soin & entretien',
      text: 'Diagnostic capillaire et soins Kérastase pour réparer, hydrater et sublimer toutes les natures de cheveux.',
      link: { label: 'Découvrir', href: '#' },
    },
  ],
};

/** Galerie du salon (max 5 photos) — richfield SALON (spec 3.b). */
export const SALON_GALLERY = [
  { src: img('photo-salon.jpg'), alt: 'Intérieur du salon' },
  { src: collection('23-05_TCHIP6514.jpg'), alt: 'Espace coiffage' },
  { src: collection('23-05_TCHIP7070.jpg'), alt: 'Bacs à shampooing' },
  { src: collection('23-05_TCHIP6198.jpg'), alt: 'Coin accueil' },
  { src: collection('23-05_TCHIP6892.jpg'), alt: 'Vitrine du salon' },
];

/** Diaporama collection — richfield COLLECTION (spec 3.h). */
export const COLLECTION_SLIDES = {
  eyebrow: '03 · Nos inspirations et tendances',
  slides: [
    {
      src: collection('23-05_TCHIP5642.jpg'),
      title: { line1: 'Cuivré', highlight: 'naturel' },
      text: "Des longs ondulés aux reflets chauds, réalisés avec les colorations L'Oréal Professionnel.",
    },
    {
      src: collection('23-05_TCHIP6379.jpg'),
      title: { line1: 'Carré', highlight: 'doré' },
      text: 'Un carré court lumineux aux reflets dorés — la coupe signature de la collection 2026.',
    },
    {
      src: collection('23-05_TCHIP6801.jpg'),
      title: { line1: 'Bob', highlight: 'wavy' },
      text: "Mi-long ondulé, texture naturelle. La coupe qui fait l'unanimité, à prix Tchip.",
    },
    {
      src: collection('23-05_TCHIP6984.jpg'),
      title: { line1: 'Balayage', highlight: 'soleil' },
      text: "L'effet bonne mine toute l'année. Shampooing + coupe + balayage à partir de 60 €.",
    },
    {
      src: collection('23-05_TCHIP7070.jpg'),
      title: { line1: 'Couleur', highlight: 'intense' },
      text: "Couleur pleine longue durée, signée L'Oréal Pro. Résultat salon, prix Tchip.",
    },
    {
      src: collection('23-05_TCHIP5665.jpg'),
      title: { line1: 'Court', highlight: 'audacieux' },
      text: 'Une coupe courte franche et assumée. Pour les femmes qui osent, à prix canon.',
    },
  ],
};

/** Réassurance — quatre items identiques sur les deux pages. Le premier titre
 * dépend de `brand.placeCount` (voir `roundedSalonCount` ci-dessus), d'où la
 * fonction plutôt qu'un tableau figé. */
export const reassuranceItems = (placeCount) => [
  { icon: img('icons/icon_salons.svg'), title: `+${roundedSalonCount(placeCount)} SALONS`, text: 'partout en France' },
  { icon: img('icons/icon_anniv.svg'), title: '30 ANNÉES', text: "d'expertise depuis 1996" },
  {
    icon: img('icons/icon_produits.svg'),
    title: 'PRODUITS PRO',
    text: "L'Oréal Professionnel & Kérastase",
  },
  {
    icon: img('icons/icon_calendrier.svg'),
    title: 'SANS RDV',
    text: "Prenez place dans la file d'attente",
  },
];

/* ───────────────────────────── Goodays ───────────────────────────── */

/**
 * Les notes Goodays viennent désormais du state : `merchant.goodaysRating` /
 * `goodaysReviewCount` par salon dans le listing, `state.goodays.rating` /
 * `reviewCount` sur la fiche, `state.goodays.network` pour l'agrégat réseau —
 * tous injectés côté serveur par `services/react_manager.js`.
 *
 * Seul le libellé reste éditorial ici ; les valeurs 4,6 / 892 de la maquette
 * ont été retirées pour qu'aucun rendu ne puisse retomber dessus par accident.
 */
export const NETWORK_RATING = {
  label: "L'avis des clientes",
};

export const NETWORK_REVIEWS = {
  title: { line1: "Ce qu'elles", highlight: 'en pensent.' },
  // Spec 2.e : c'est la moyenne réseau. La maquette écrivait « sur ce salon » et
  // reprenait les 892 avis de Bastille — corrigé (plan §13 Phase 2).
  subtitle: 'Les avis de nos clientes sur le réseau Tchip, certifiés par Goodays.',
  reviews: [
    {
      rating: 5,
      content: "Accueil au top, j'attendais 10 minutes à peine grâce à l'appli file d'attente.",
      sender: 'Camille R.',
    },
    {
      rating: 5,
      content: 'Très bon rapport qualité-prix, le salon est lumineux et propre.',
      sender: 'Sarah B.',
    },
    {
      rating: 4,
      content: "Sans rendez-vous mais jamais d'attente interminable. Le forfait coupe + brushing est top.",
      sender: 'Lina M.',
    },
    {
      rating: 5,
      content: 'Mon balayage est exactement ce que je voulais, je recommande ce salon les yeux fermés.',
      sender: 'Amina K.',
    },
  ],
};

/** Variante « ce salon » de l'onglet Avis de la page détail. */
export const SALON_REVIEWS = {
  title: { line1: "Ce qu'elles", highlight: 'en pensent.' },
  subtitle: 'Les avis de nos clientes sur ce salon, certifiés par Goodays.',
  reviews: NETWORK_REVIEWS.reviews,
};

/**
 * §9.2.a : les visuels des cartes du listing sont tirés d'un pool de 10 images.
 * Le client doit fournir les définitives, de préférence par URL pour qu'elles
 * soient remplaçables sans redéploiement (§17.4).
 */
export const LISTING_PHOTO_POOL = [
  img('photo-salon.jpg'),
  collection('23-05_TCHIP5820.jpg'),
  collection('23-05_TCHIP6379.jpg'),
  collection('23-05_TCHIP6801.jpg'),
  collection('23-05_TCHIP6984.jpg'),
  collection('23-05_TCHIP6403.jpg'),
  collection('23-05_TCHIP6198.jpg'),
  collection('23-05_TCHIP6514.jpg'),
  collection('23-05_TCHIP6892.jpg'),
  collection('23-05_TCHIP7070.jpg'),
];

/**
 * Le tirage doit être stable : une même place montre toujours la même photo,
 * y compris entre le rendu serveur et l'hydratation. On dérive donc l'index de
 * l'identifiant plutôt que de tirer au hasard — `Math.random()` produirait une
 * discordance d'hydratation à chaque rendu.
 */
export const photoForPlace = (placeId) => {
  const key = String(placeId || '');
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) % 100000;
  }
  return LISTING_PHOTO_POOL[hash % LISTING_PHOTO_POOL.length];
};

export const LISTING_COPY = {
  eyebrow: 'Trouver un salon',
  title: { line1: 'Tous nos salons', highlight: 'près de vous' },
  cityLinksTitle: 'Nos salons par ville',
  emptyBeforeSearch:
    'Indiquez une ville, un code postal ou activez la géolocalisation pour voir les salons les plus proches.',
  emptyAfterSearch: 'Aucun salon trouvé autour de ce point. Essayez une ville voisine.',
  searchNotFound: "Nous n'avons pas trouvé ce lieu — vérifiez l'orthographe ou essayez une ville proche.",
};
